/**
 * Scroll interactions for the Kalai services page.
 *
 * Two independent behaviours, both progressive enhancements:
 *  1. Reveal — elements tagged with [data-kalai-reveal] fade and rise into place once.
 *  2. Section nav — the sticky category nav highlights whichever group owns the viewport.
 *
 * Everything degrades to "fully visible, no highlight" when JS or IntersectionObserver
 * is unavailable, and animation is skipped entirely under prefers-reduced-motion.
 */

const REVEAL_SELECTOR = '[data-kalai-reveal]';
const REVEALED_CLASS = 'kalai-revealed';

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** @type {IntersectionObserver | null} */
let revealObserver = null;

/** @type {(() => void) | null} */
let teardownSectionNav = null;

function revealImmediately(/** @type {Element} */ root) {
  for (const el of root.querySelectorAll(REVEAL_SELECTOR)) el.classList.add(REVEALED_CLASS);
}

function observeReveals(/** @type {Element | Document} */ root) {
  if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
    revealImmediately(/** @type {Element} */ (root));
    return;
  }

  revealObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add(REVEALED_CLASS);
        revealObserver?.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
  );

  for (const el of root.querySelectorAll(REVEAL_SELECTOR)) {
    if (!el.classList.contains(REVEALED_CLASS)) revealObserver.observe(el);
  }
}

/**
 * Pins the category nav to the top of the viewport and highlights the link whose
 * target group is closest to the top.
 */
function setupSectionNav() {
  teardownSectionNav?.();
  teardownSectionNav = null;

  const nav = /** @type {HTMLElement | null} */ (document.querySelector('[data-kalai-nav]'));
  if (!nav) return;

  const slot = /** @type {HTMLElement | null} */ (nav.closest('.kalai-nav-slot'));

  const links = /** @type {HTMLAnchorElement[]} */ ([...nav.querySelectorAll('a[href^="#"]')]);
  const targets = links
    .map((link) => ({ link, section: document.getElementById(decodeURIComponent(link.hash.slice(1))) }))
    .filter((entry) => entry.section instanceof HTMLElement);

  const pin = () => {
    if (!slot) return;
    const pinned = nav.classList.contains('is-pinned');
    if (!pinned) slot.style.minHeight = `${nav.offsetHeight}px`;

    const rect = slot.getBoundingClientRect();
    const shouldPin = rect.top <= 0;
    if (shouldPin !== pinned) nav.classList.toggle('is-pinned', shouldPin);
    if (shouldPin) {
      nav.style.setProperty('--kalai-nav-left', `${rect.left}px`);
      nav.style.setProperty('--kalai-nav-width', `${rect.width}px`);
    }
  };

  const list = /** @type {HTMLElement | null} */ (nav.querySelector('.kalai-nav__list'));
  /** @type {HTMLAnchorElement | null} */
  let currentLink = null;

  const highlight = () => {
    if (targets.length === 0) return;
    // The anchor line sits a third of the way down the viewport, which matches
    // where a reader's attention lands while scrolling.
    const anchorLine = window.innerHeight / 3;
    let current = targets[0];

    for (const entry of targets) {
      if (/** @type {HTMLElement} */ (entry.section).getBoundingClientRect().top <= anchorLine) current = entry;
    }

    const next = current?.link ?? null;
    if (next === currentLink) return;
    currentLink = next;

    for (const { link } of targets) link.classList.toggle('is-current', link === next);

    // On narrow screens the list scrolls horizontally, so keep the active link in view.
    if (list && next && list.scrollWidth > list.clientWidth) {
      const offset = next.offsetLeft - list.clientWidth / 2 + next.offsetWidth / 2;
      list.scrollTo({ left: Math.max(0, offset), behavior: 'smooth' });
    }
  };

  const update = () => {
    pin();
    highlight();
  };

  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      update();
      ticking = false;
    });
  };

  update();
  // Above 990px the theme scrolls `.page-wrapper` rather than the document, and scroll
  // events don't bubble — capturing at the document catches both cases.
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  window.addEventListener('resize', onScroll, { passive: true });

  teardownSectionNav = () => {
    document.removeEventListener('scroll', onScroll, { capture: true });
    window.removeEventListener('resize', onScroll);
  };
}

function init(/** @type {Element | Document} */ root = document) {
  observeReveals(root);
}

/**
 * Opens and reveals the row referenced by the URL hash, so links coming from other
 * pages land directly on a specific treatment.
 */
function openDeepLinkedRow() {
  const hash = location.hash.slice(1);
  if (!hash) return;

  const target = document.getElementById(decodeURIComponent(hash));
  if (!target?.matches('.kalai-service, .kalai-package, .kalai-sub')) return;

  if (target instanceof HTMLDetailsElement) target.open = true;
  target.classList.add(REVEALED_CLASS);

  requestAnimationFrame(() => {
    target.scrollIntoView({ block: 'start' });
    target.classList.remove('kalai-target');
    // Restarting the animation requires a reflow between removing and re-adding.
    void target.offsetWidth;
    target.classList.add('kalai-target');
  });
}

init();
setupSectionNav();
openDeepLinkedRow();
window.addEventListener('hashchange', openDeepLinkedRow);

// The theme editor swaps section markup in and out without a page load.
document.addEventListener('shopify:section:load', (event) => {
  const target = /** @type {CustomEvent} */ (event).target;
  if (target instanceof Element) init(target);
  setupSectionNav();
});
